import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useGetCollectionsQuery } from '../../api/reports/ReportApi';
import { CollectionRow } from '../../types/reports';
import { exportToExcel } from '../../utils/exportExcel';

const money = (value?: number | null) => Number(value || 0).toFixed(2);
const today = () => new Date().toISOString().slice(0, 10);

const headCell = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  px: 1.5,
  py: 1,
};

const paymentLabel: Record<string, string> = {
  CASH: 'Cash',
  UPI_CARD: 'UPI / Card',
  CREDIT: 'Credit',
  EMI: 'EMI (financed)',
  SPLIT: 'Split (more than one mode)',
};

/**
 * The day book: how the day's money arrived.
 *
 * <p>The report exists because "sales" and "collections" are different numbers and
 * a shop closing its till needs the second one. A credit bill is revenue with
 * nothing in the drawer; an EMI bill is revenue the bank still owes; a customer
 * clearing last week's balance is money in the drawer that belongs to no bill
 * raised today. All three are shown separately, and the bottom line is what the
 * cash should count to.
 */
const DayBookReport = () => {
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [allTime, setAllTime] = useState(false);

  const { data, isFetching } = useGetCollectionsQuery(
    allTime ? {} : { fromDate: fromDate || undefined, toDate: toDate || undefined },
  );

  const rows: CollectionRow[] = data?.rows ?? [];

  const download = () =>
    exportToExcel(
      [
        ...rows.map((row) => ({
          'Payment Type': paymentLabel[row.paymentType] ?? row.paymentType,
          Bills: row.billCount,
          Billed: row.billedAmount,
          Collected: row.collectedAmount,
          'Still to come': row.pendingAmount,
        })),
        {
          'Payment Type': 'Received against older bills',
          Bills: '',
          Billed: '',
          Collected: data?.receivedAgainstOldBills ?? 0,
          'Still to come': '',
        },
        {
          'Payment Type': 'TOTAL COLLECTED',
          Bills: data?.billCount ?? 0,
          Billed: data?.billedTotal ?? 0,
          Collected: data?.totalCollected ?? 0,
          'Still to come': '',
        },
      ],
      `day-book-${allTime ? 'all-time' : `${fromDate}-to-${toDate}`}`,
    );

  return (
    <Box>
      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          label="From"
          size="small"
          type="date"
          disabled={allTime}
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(event) => setFromDate(event.target.value)}
        />
        <TextField
          label="To"
          size="small"
          type="date"
          disabled={allTime}
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(event) => setToDate(event.target.value)}
        />
        <Chip
          label={allTime ? 'All time' : 'Date range'}
          color={allTime ? 'primary' : 'default'}
          variant={allTime ? 'filled' : 'outlined'}
          onClick={() => setAllTime((value) => !value)}
        />
        <Button
          size="small"
          onClick={() => {
            setAllTime(false);
            setFromDate(today());
            setToDate(today());
          }}
        >
          Today
        </Button>
        <Box flexGrow={1} />
        <Button size="small" startIcon={<DownloadIcon />} onClick={download} disabled={rows.length === 0}>
          Excel
        </Button>
      </Stack>

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Payment Type', 'Bills', 'Billed', 'Collected', 'Still to come'].map((head) => (
                <TableCell key={head} sx={headCell} align={head === 'Payment Type' ? 'left' : 'right'}>
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  {isFetching ? 'Loading...' : 'No bills in this period'}
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.paymentType} hover>
                <TableCell>{paymentLabel[row.paymentType] ?? row.paymentType}</TableCell>
                <TableCell align="right">{row.billCount}</TableCell>
                <TableCell align="right">{money(row.billedAmount)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {money(row.collectedAmount)}
                </TableCell>
                <TableCell align="right" sx={{ color: Number(row.pendingAmount) > 0 ? 'warning.main' : 'text.secondary' }}>
                  {money(row.pendingAmount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.main">
              RECEIVED AGAINST OLDER BILLS
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Customers clearing a credit balance from an earlier day
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {Object.keys(data?.receipts ?? {}).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing received in this period.
              </Typography>
            ) : (
              Object.entries(data?.receipts ?? {}).map(([mode, amount]) => (
                <Stack key={mode} direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2">{paymentLabel[mode] ?? mode}</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {money(amount as number)}
                  </Typography>
                </Stack>
              ))
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%', bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.main">
              SUMMARY
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2">Bills raised</Typography>
              <Typography variant="body2">{data?.billCount ?? 0}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2">Billed</Typography>
              <Typography variant="body2">{money(data?.billedTotal)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2">Collected on those bills</Typography>
              <Typography variant="body2">{money(data?.collectedOnBills)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2">Received on older bills</Typography>
              <Typography variant="body2">{money(data?.receivedAgainstOldBills)}</Typography>
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Total collected
              </Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                {money(data?.totalCollected)}
              </Typography>
            </Stack>
            {/* Cash paid to a supplier leaves the same drawer the counter fills, so it
                has to come off before this figure can be counted against what is there. */}
            {Number(data?.cashPaidOut) > 0 && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="body2">Cash paid to suppliers</Typography>
                <Typography variant="body2" color="error.main">
                  - {money(data?.cashPaidOut)}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={700}>
                Cash that should be in hand
              </Typography>
              <Typography variant="h6" fontWeight={700} color="success.main">
                {money(data?.cashInHand)}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Cash sales {money(data?.cashSales)} + cash received {money(data?.cashReceipts)}
              {Number(data?.cashPaidOut) > 0 ? ` - cash paid out ${money(data?.cashPaidOut)}` : ''}
            </Typography>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DayBookReport;
